import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import classes from "./WeatherPage.module.css";
import { ArrowLeftOutlined, EnvironmentOutlined } from "@ant-design/icons";

const WEATHER_API_KEY = process.env.REACT_APP_FORECAST_API_KEY;
const GEO_API_KEY = process.env.REACT_APP_FORECAST_GEO_API_KEY;

interface WeatherData {
  main: { temp: number; feels_like: number; humidity: number };
  weather: { main: string; description: string; icon: string }[];
  wind: { speed: number };
  name: string;
}

interface ForecastItem {
  dt: number;
  dt_txt: string;
  main: { temp_min: number; temp_max: number };
  weather: { main: string; description: string; icon: string }[];
}

interface LocationSuggestion {
  name: string;
  state?: string;
  country: string;
  lat: number;
  lon: number;
}

const DEFAULT_CITY = "Prishtina";

const WeatherPage: React.FC = () => {
  const [current, setCurrent] = useState<WeatherData | null>(null);
  const [forecast, setForecast] = useState<ForecastItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [searchInput, setSearchInput] = useState(DEFAULT_CITY);
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [selectedLocation, setSelectedLocation] =
    useState<LocationSuggestion | null>(null);
  const [mapSrc, setMapSrc] = useState<string | null>(null);

  const weatherBackgrounds: { [key: string]: string } = {
    Clear: "linear-gradient(to top, #fddb92, #d1fdff)",
    Clouds: "linear-gradient(to top, #d7d2cc, #304352)",
    Rain: "linear-gradient(to top, #4e54c8, #8f94fb)",
    Drizzle: "linear-gradient(to top, #89f7fe, #66a6ff)",
    Thunderstorm: "linear-gradient(to top, #0f2027, #203a43, #2c5364)",
    Snow: "linear-gradient(to top, #e6dada, #274046)",
    Mist: "linear-gradient(to top, #606c88, #3f4c6b)",
    Smoke: "linear-gradient(to top, #56ab2f, #a8e063)",
    Haze: "linear-gradient(to top, #3e5151, #decba4)",
    Dust: "linear-gradient(to top, #ba8b02, #181818)",
    Fog: "linear-gradient(to top, #3e5151, #decba4)",
    Sand: "linear-gradient(to top, #c79081, #dfa579)",
    Ash: "linear-gradient(to top, #434343, #000000)",
    Squall: "linear-gradient(to top, #373b44, #4286f4)",
    Tornado: "linear-gradient(to top, #000000, #434343)",
  };

  const navigate = useNavigate();
  const fetchWeatherByLocation = async (lat: number, lon: number) => {
    setLoading(true);
    try {
      const currentRes = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&units=metric&appid=${WEATHER_API_KEY}`,
      );
      if (!currentRes.ok) {
        const errorData = await currentRes.json();
        throw new Error(errorData.message || "Failed to fetch current weather");
      }
      const currentData: WeatherData = await currentRes.json();
      setCurrent(currentData);

      const forecastRes = await fetch(
        `https://api.openweathermap.org/data/2.5/forecast?lat=${lat}&lon=${lon}&units=metric&appid=${WEATHER_API_KEY}`,
      );
      if (!forecastRes.ok) {
        const errorData = await forecastRes.json();
        throw new Error(errorData.message || "Failed to fetch forecast");
      }
      const forecastData = await forecastRes.json();

      const daily: ForecastItem[] = [];
      const seenDates = new Set();
      forecastData.list.forEach((item: ForecastItem) => {
        const date = item.dt_txt.split(" ")[0];
        if (!seenDates.has(date) && item.dt_txt.includes("12:00:00")) {
          daily.push(item);
          seenDates.add(date);
        }
      });
      setForecast(daily.slice(0, 5));
      setErrorMsg("");
    } catch (err: any) {
      console.error(err);
      setErrorMsg(
        err.message || "Unable to load weather data for this location.",
      );
      setCurrent(null);
      setForecast([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async (query: string) => {
    if (!query) {
      setSuggestions([]);
      return;
    }
    try {
      const encodedQuery = encodeURIComponent(query.trim());
      const res = await fetch(
        `https://api.openweathermap.org/geo/1.0/direct?q=${encodedQuery}&limit=5&appid=${GEO_API_KEY}`,
      );
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.message || "Failed to fetch location suggestions",
        );
      }
      const data: LocationSuggestion[] = await res.json();
      setSuggestions(data);
    } catch (err: any) {
      console.error(err);
      setSuggestions([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    fetchSuggestions(e.target.value);
  };

  const handleSelectLocation = (location: LocationSuggestion) => {
    setSelectedLocation(location);
    setSearchInput(
      `${location.name}${location.state ? ", " + location.state : ""}, ${location.country}`,
    );
    setSuggestions([]);
    fetchWeatherByLocation(location.lat, location.lon);
  };

  useEffect(() => {
    const loadDefault = async () => {
      try {
        const res = await fetch(
          `https://api.openweathermap.org/geo/1.0/direct?q=${encodeURIComponent(
            DEFAULT_CITY,
          )}&limit=1&appid=${GEO_API_KEY}`,
        );
        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.message || "Failed to load default city");
        }
        const data: LocationSuggestion[] = await res.json();
        if (data[0]) {
          setSelectedLocation(data[0]);
          setSearchInput(`${data[0].name}, ${data[0].country}`);
          fetchWeatherByLocation(data[0].lat, data[0].lon);
        } else {
          setErrorMsg("Default city data not found.");
        }
      } catch (err: any) {
        console.error(err);
        setErrorMsg(err.message || "Unable to load default city data.");
      }
    };

    const loadWeatherByBrowserLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            fetchWeatherByLocation(latitude, longitude);
            setSelectedLocation({
              name: "Current Location",
              country: "",
              lat: latitude,
              lon: longitude,
            });
          },
          () => {
            console.warn(
              "Geolocation denied or unavailable, using default city.",
            );
            loadDefault();
          },
        );
      } else {
        console.warn("Geolocation not supported, using default city.");
        loadDefault();
      }
    };

    loadWeatherByBrowserLocation();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      const { lat, lon } = selectedLocation;
      setMapSrc(
        `https://www.openstreetmap.org/export/embed.html?bbox=${
          lon - 0.1
        }%2C${lat - 0.1}%2C${lon + 0.1}%2C${
          lat + 0.1
        }&layer=mapnik&marker=${lat}%2C${lon}`,
      );
    }
  }, [selectedLocation]);

  const getDayName = (dt_txt: string) =>
    new Date(dt_txt).toLocaleDateString("en-US", { weekday: "short" });

  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (suggestions.length === 0) return;

      if (event.key === "Escape") {
        setSuggestions([]);
        setHighlightedIndex(-1);
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev < suggestions.length - 1 ? prev + 1 : 0,
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : suggestions.length - 1,
        );
      } else if (event.key === "Enter") {
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelectLocation(suggestions[highlightedIndex]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [suggestions, highlightedIndex]);

  return (
    <div
      className={classes.mainDiv}
      style={{
        background:
          current && weatherBackgrounds[current.weather[0].main]
            ? weatherBackgrounds[current.weather[0].main]
            : "#000",
      }}
    >
      <div className={classes.header}>
        <div className={classes.backRow} onClick={() => navigate("/lajmet")}>
          <div className={classes.circleButton}>
            <ArrowLeftOutlined style={{ color: "white", fontSize: "1.2rem" }} />
          </div>
          <span className={classes.backText}>Rikthehu ne faqe</span>
        </div>

        <div className={classes.centerBlock}>
          <h2 className={classes.headerTitle}>Parashikimi i Motit</h2>

          <div className={classes.topRow}>
            <div className={classes.searchRow}>
              <input
                type="text"
                value={searchInput}
                onChange={handleInputChange}
                placeholder="Search worldwide..."
                className={classes.cityInput}
              />
              {suggestions.length > 0 && (
                <ul className={classes.suggestionsList}>
                  {suggestions.map((s, idx) => (
                    <li
                      key={idx}
                      onClick={() => handleSelectLocation(s)}
                      className={
                        idx === highlightedIndex ? classes.highlighted : ""
                      }
                    >
                      <EnvironmentOutlined /> {s.name}
                      {s.state ? `, ${s.state}` : ""}, {s.country}
                    </li>
                  ))}
                </ul>
              )}
              {mapSrc && (
                <div className={classes.mapContainer}>
                  <iframe
                    key={mapSrc}
                    title="location map"
                    width="100%"
                    height="200"
                    frameBorder="0"
                    scrolling="no"
                    marginHeight={0}
                    marginWidth={0}
                    src={mapSrc}
                  ></iframe>
                </div>
              )}
            </div>

            {current && (
              <div className={classes.currentCard}>
                <h3 className={classes.cityName}>
                  {current.name}
                  {selectedLocation?.state ? `, ${selectedLocation.state}` : ""}
                  , {selectedLocation?.country}
                </h3>
                <img
                  src={`https://openweathermap.org/img/wn/${current.weather[0].icon}@2x.png`}
                  alt={current.weather[0].description}
                />
                <div className={classes.temp}>
                  {Math.round(current.main.temp)}°C
                </div>
                <div className={classes.description}>
                  {current.weather[0].description}
                </div>
                <div className={classes.details}>
                  Feels like: {Math.round(current.main.feels_like)}°C |
                  Humidity: {current.main.humidity}% | Wind:{" "}
                  {current.wind.speed} m/s
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className={classes.grid}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={classes.skeletonCard}>
              <div className={classes.skeletonTitle} />
            </div>
          ))}
        </div>
      ) : (
        <div className={classes.grid}>
          {forecast.map((day) => (
            <div key={day.dt} className={classes.card}>
              <img
                src={`https://openweathermap.org/img/wn/${day.weather[0].icon}@2x.png`}
                alt={day.weather[0].description}
              />
              <span className={classes.cardTitle}>
                {getDayName(day.dt_txt)}
              </span>
              <div className={classes.cardInfo}>
                {Math.round(day.main.temp_min)}°C |{" "}
                {Math.round(day.main.temp_max)}°C
                <br />
                {day.weather[0].main}
              </div>
            </div>
          ))}
        </div>
      )}

      {errorMsg && <p className={classes.errorMsg}>{errorMsg}</p>}
    </div>
  );
};

export default WeatherPage;
